// ONE definition of "these two contacts are the same person".
//
// Two screens ask that question:
//   - src/pages/Signup.jsx          (the customer creating an account)
//   - src/pages/StaffNewOrder.jsx   (the admin typing a walk-in)
//
// The rule used to live only in StaffNewOrder, as a useMemo. The customer
// side had nothing, which is how Monti Blumberg ended up with two accounts
// on the same phone number and paid $190 a cycle instead of $95 for three
// weeks before anyone noticed.
//
// THE RULE: same email (trimmed, lowercased) OR same last 7 digits of the
// phone. Seven digits because the same handset is reached whether someone
// writes +17862559419, (786) 255-9419 or 255-9419 — the country code and
// the area code are exactly where people differ in how they type a number.
//
// The signup screen can't read other people's profiles (RLS, and it would
// be a disclosure), so it sends these normalised keys to the
// `signup_contact_taken` RPC, which answers two booleans and nothing else.
// The normalisation therefore has one definition — this file — and the SQL
// only normalises the stored side, which it has to do itself.

import { supabase } from './supabase'

/** Trimmed, lowercased email — '' when there is nothing usable. */
export function emailKey(raw) {
  return String(raw || '').trim().toLowerCase()
}

/**
 * Last 7 digits of a phone number, or '' when fewer than 7 digits were
 * typed. Returning '' (rather than a 3-digit prefix) is what stops a
 * half-typed number from matching half the customer base.
 */
export function phoneKey(raw) {
  const digits = String(raw || '').replace(/\D/g, '')
  return digits.length >= 7 ? digits.slice(-7) : ''
}

/** Does this profile row match the typed email or phone? */
export function sameContact(row, { email, phone } = {}) {
  if (!row) return false
  const e = emailKey(email)
  const p = phoneKey(phone)
  if (!e && !p) return false
  if (e && e === emailKey(row.email)) return true
  if (p && p === phoneKey(row.phone)) return true
  return false
}

/**
 * First row in `rows` that matches, or null.
 *
 * Guest profiles are NOT skipped here. "Skip guests" is a rule of the
 * signup screen — a walk-in that staff took over the phone must still be
 * able to create their own account later — and it lives in the
 * signup_contact_taken SQL. On the admin screen the opposite is true: a
 * matching guest profile is exactly what the admin wants to be offered, so
 * that the order attaches to it instead of creating yet another row.
 */
export function findContactMatch(rows, { email, phone } = {}) {
  if (!Array.isArray(rows)) return null
  return rows.find((r) => sameContact(r, { email, phone })) || null
}

/**
 * Ask the server whether this email or phone already belongs to an account.
 *
 * Returns `{ emailTaken, phoneTaken }`. The server deliberately returns
 * nothing else — not the other account's email, name or id. "An account
 * exists with this number" is all the signup screen is allowed to say.
 *
 * Fails OPEN: if the RPC errors, both flags come back false. A network blip
 * must never stop someone from creating an account.
 */
export async function checkContactTaken({ email, phone } = {}) {
  const e = emailKey(email)
  const p = phoneKey(phone)
  if (!e && !p) return { emailTaken: false, phoneTaken: false }
  try {
    const { data, error } = await supabase.rpc('signup_contact_taken', {
      p_email: e || null,
      p_phone_last7: p || null,
    })
    if (error) throw error
    const row = Array.isArray(data) ? data[0] : data
    return {
      emailTaken: !!row?.email_taken,
      phoneTaken: !!row?.phone_taken,
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[contactMatch] check failed, letting signup through', err?.message || err)
    return { emailTaken: false, phoneTaken: false }
  }
}
